<template>
  <v-row align="center" justify="center" class="search-criteria">
    <v-col cols="1" class="text-left" v-if="showAddButton">
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn v-on="on" large color="primary" icon @click="reset()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Clear") }}</span>
      </v-tooltip>
    </v-col>
    <v-col>
      <v-autocomplete
        v-model="conditionItem.fieldCode"
        :items="fieldList"
        item-text="DisplayText"
        item-value="Code"
        :label="$t('Field')"
        :disabled="disableFieldSelect"
        :error-messages="fieldCodeErrors"
        @change="fieldCodeChange"
        :menu-props="{ minWidth: 600, maxWidth: 600 }"
        :loading="isFieldListLoading"
      ></v-autocomplete>
    </v-col>
    <v-col v-if="showSearchOperatorSelect">
      <v-select
        v-model="conditionItem.searchOperatorCode"
        :items="filteredSearchOperators"
        :label="$t('Operator')"
        item-text="Title"
        item-value="Code"
        :disabled="disableSearchOperatorSelect"
        :error-messages="searchOperatorCodeErrors"
      ></v-select>
    </v-col>
    <v-col v-if="showValueField">
      <v-text-field
        v-model="conditionItem.fieldDisplayValue"
        v-if="fieldType == 0"
        :label="$t('Value')"
        :error-messages="fieldDisplayValueErrors"
      ></v-text-field>
      <v-text-field
        type="number"
        v-model="fieldValueNumeric"
        v-if="fieldType == 2 && !ISOCompliance"
        :label="$t('Value')"
        :error-messages="fieldValueErrors"
        @blur="validateNumericScale"
      ></v-text-field>
      <SearchCriteriaNumericInputISO
        v-else-if="fieldType == 2 && ISOCompliance"
        :conditionItem.sync="conditionItem"
        :scale="conditionItem.scale"
        :labelText="$t('Value')"
        :error-messages="fieldValueErrors"
      />
      <v-autocomplete
         ref="autocomplete"
        :items="listElements"
        item-text="text"
        item-value="text"
        :search-input.sync="search"
        :loading="isLoading"
        v-model="pivotAutoCompleteFieldValue"
        :multiple="multiValue"
        :chips="multiValue"
        :deletable-chips="multiValue"
        v-if="fieldType >= 3"
        :label="$t('Value')"
        :cache-items="hasPerformanceQuery && multiValue"
        :error-messages="fieldValueErrors"
        @change="multivalueToString"
      ></v-autocomplete>
      <BaseDatePicker
        v-if="fieldType == 1 && !ISOCompliance"
        :label="$t('Value')"
        :error-messages="fieldValueErrors"
        v-model="conditionItem.fieldDisplayValue"
      />
      <DatePickerISOCompliant
        v-if="fieldType == 1 && ISOCompliance"
        :label="$t('Value')"
        :error-messages="fieldValueErrors"
        v-model="conditionItem.fieldDisplayValue"
      />
    </v-col>
    <v-col
      cols="1"
      v-if="
        fieldType == 1 && !isEmptyNotEmptyOperator && conditionItem.useToday
      "
    >
      <v-text-field
        type="number"
        v-model="conditionItem.daysBeforeAfterToday"
      ></v-text-field>
    </v-col>
    <v-col
      cols="3"
      v-if="
        fieldType == 1 && !isEmptyNotEmptyOperator && conditionItem.useToday
      "
    >
      <v-radio-group v-model="conditionItem.beforeAfter" row>
        <v-radio v-bind:label="$t('DaysBeforeToday')" value="B"></v-radio>
        <v-radio v-bind:label="$t('DaysAfterToday')" value="A"></v-radio>
      </v-radio-group>
    </v-col>
    <v-col cols="3" v-if="canUseVariables && conditionItem.useVariables">
        <SearchVariableInput :conditionItem.sync="conditionItem" :error-messages="fieldValueErrors" />
    </v-col>
    <v-col class="py-2" v-if="fieldType != 3">
    <template v-if="isCheckboxOptionVisible(checkboxValueOptions.MySelf)">
      <v-tooltip top>
        <template v-slot:activator="{ on }">
        <span v-on="on">
            <v-checkbox
                class="search-checkboxes"
                v-model="conditionItem.myselfOption"
                :label="$t('Myself')"
                dense
                hide-details="auto"
                v-on="on"
                :value="MyselfOptionValues.MySelf"
                @click="optionCheckboxSelect(checkboxValueOptions.MySelf)"
            ></v-checkbox>
        </span>
        </template>
        <span>{{ $t("WhatIsMySelfHelpMessage") }}</span>
      </v-tooltip>
      <v-tooltip top v-if="showMySelfId">
        <template v-slot:activator="{ on }">
        <span v-on="on">
            <v-checkbox
                class="search-checkboxes"
                :label="$t('MyselfId')"
                v-model="conditionItem.myselfOption"
                dense
                hide-details="auto"
                v-on="on"
                :value="MyselfOptionValues.MySelf_Id"
                @click="optionCheckboxSelect(checkboxValueOptions.MySelfId)"
            ></v-checkbox>
        </span>
        </template>
        <span>{{ $t("WhatIsMySelfIdHelpMessage") }}</span>
      </v-tooltip>
    </template>
    <template v-if="isCheckboxOptionVisible(checkboxValueOptions.Today)">
        <v-checkbox
            class="search-checkboxes"
            :label="$t('Today')"
            dense
            hide-details="auto"
            @click="optionCheckboxSelect(checkboxValueOptions.Today)"
            v-model="conditionItem.useToday"
        ></v-checkbox>
    </template>
        <template v-if="isCheckboxOptionVisible(checkboxValueOptions.Variables)">
            <v-checkbox
                class="search-checkboxes"
                :label="$t('Variables')"
                dense
                hide-details="auto"
                @click="optionCheckboxSelect(checkboxValueOptions.Variables)"
                v-model="conditionItem.useVariables"
            ></v-checkbox>
        </template>
    </v-col>
    <v-col v-if="showParameterizableValue">
      <v-switch
        v-model="conditionItem.isParameterizable"
        :label="$t('Parameterizable')"
      ></v-switch>
    </v-col>
    <v-col cols="1" align-self="center" class="text-right" v-if="showAddButton">
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            tile
            large
            color="primary"
            icon
            @click="onConditionItemAdd()"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Add") }}</span>
      </v-tooltip>
    </v-col>
  </v-row>
</template>

<script>
import ContentTypeService from '@/services/ContentTypeService';
import Utils from '@/ecm/common/Utils';
import ConditionItem from '@/ecm/models/search/ConditionItem';
import FieldType from '@/ecm/variables/FieldType';
import StandardFieldCode from '@/ecm/variables/StandardFieldCode';
import SearchConstValues from '@/ecm/variables/SearchConstValues';
import { required, requiredIf, minLength } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import MyselfOptionValues from '@/ecm/variables/MyselfOptionValues';
import DatePickerISOCompliant from '@/components/base/DatePickerISOCompliant';
import SearchCriteriaNumericInputISO from '@/components/search/inputs/SearchCriteriaNumericInputISO.vue';
import SearchVariableInput from '@/components/search/inputs/SearchVariableInput.vue';

export default {
    name: 'SearchCriteriaCondition',
    components: {
        DatePickerISOCompliant,
        SearchCriteriaNumericInputISO,
        SearchVariableInput
    },
    props: {
        fieldList: {
            type: Array,
            default: () => [],
        },
        conditionItem: {
            type: Object,
            default: () => new ConditionItem()
        },
        origin: {
            type: Number,
            default: 0,
        },
        // eslint-disable-next-line vue/prop-name-casing
        ISOCompliance: {
            type: Boolean,
            default: false,
        },
        canUseVariables: {
            type: Boolean,
            default: true
        },
        isFieldListLoading: {
            type: Boolean,
            default: false
        }
    },
    mounted() {
        this.loadEnabledAndDisabledInputs();
        this.getListElements();
        this.$v.$reset();
    },
    data() {
        return {
            isLoading: false,
            search: null,
            pivotAutoCompleteFieldValue: null,
            listElements: [],
            filteredListElements: [],
            showMySelfId: true,
            disableFieldSelect: false,
            disableSearchOperatorSelect: false,
            showSearchOperatorSelect: true,
            showParameterizableValue: false,
            showMyself: true,
            showToday: true,
            showAddButton: true,
            clearValue: true,
            emptyNotEmptyOperatorCodes: [
                's06',
                's07',
                'd07',
                'd08',
                'n07',
                'n08',
                'l03',
                'l04',
            ],
            selectedItems: [],
            checkboxValueOptions: { MySelf: 0, MySelfId: 1, Today: 2, Variables: 3 },
        };
    },
    mixins: [validationMixin],
    validations: {
        conditionItem: {
            fieldCode: { required },
            searchOperatorCode: {
                required: requiredIf(function () {
                    return this.showSearchOperatorSelect;
                }),
            },
            fieldValue: {
                required: requiredIf(function () {
                    return (
                        !this.isEmptyNotEmptyOperator
                    );
                }),
            },
            fieldDisplayValue: {
                required: requiredIf(function () {
                    return this.conditionItem.fieldCode === StandardFieldCode.Fulltext;
                }),
                minLength: minLength(1),
            },
        },
    },
    computed: {
        MyselfOptionValues() {
            return MyselfOptionValues;
        },
        fieldCodeErrors() {
            const errors = [];
            if (!this.$v.conditionItem.fieldCode.$dirty) return errors;
            !this.$v.conditionItem.fieldCode.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        searchOperatorCodeErrors() {
            const errors = [];
            if (!this.$v.conditionItem.searchOperatorCode.$dirty) return errors;
            !this.$v.conditionItem.searchOperatorCode.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        fieldValueErrors() {
            const errors = [];
            if (!this.$v.conditionItem.fieldValue.$dirty) return errors;
            !this.$v.conditionItem.fieldValue.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        fieldDisplayValueErrors() {
            const errors = [];
            if (!this.$v.conditionItem.fieldDisplayValue.required) {
                errors.push(this.$t('RequiredField'));
            }

            if (!this.$v.conditionItem.fieldDisplayValue.minLength) {
                errors.push(this.$t('SearchMinLenghtException'));
            }

            return errors;
        },
        propsToWatch() {
            return (
                this.conditionItem.fieldDisplayValue +
                this.conditionItem.myselfOption +
                this.conditionItem.useToday +
                this.conditionItem.daysBeforeAfterToday +
                this.conditionItem.beforeAfter +
                this.conditionItem.useVariables
            );
        },
        selectedFieldCode() {
            return this.conditionItem.fieldCode;
        },
        filteredSearchOperators() {
            if (this.fieldType !== '') {
                const allSearchOperators = this.$store.getters['account/searchOperators'];
                if (this.conditionItem.fieldCode == StandardFieldCode.Fulltext) {
                    return allSearchOperators.filter((f) => f.Code == 'f01');
                }
                if (
                    this.conditionItem.fieldCode == StandardFieldCode.CreationDate ||
                    this.conditionItem.fieldCode == StandardFieldCode.Author ||
                    this.conditionItem.fieldCode == StandardFieldCode.DocumentId
                ) {
                    return allSearchOperators.filter(
                        (f) =>
                            Utils.text.contains(f.FieldTypes, this.fieldType) &&
                            f.Code != 's06' &&
                            f.Code != 's07' &&
                            f.Code != 'd07' &&
                            f.Code != 'd08' &&
                            f.Code != 'n07' &&
                            f.Code != 'n08'
                    );
                }
                return allSearchOperators.filter((f) =>
                    Utils.text.contains(f.FieldTypes, this.fieldType)
                );
            } else return [];
        },
        isEmptyNotEmptyOperator() {
            if (this.conditionItem.searchOperatorCode == '') {
                return false;
            }
            return Utils.text.contains(
                this.emptyNotEmptyOperatorCodes,
                this.conditionItem.searchOperatorCode
            );
        },
        fieldValueNumeric: {
            // getter
            get: function () {
                if (!this.conditionItem.fieldDisplayValue) return '';

                var userDecSep = this.$store.getters['account/user'].MetricDecimalSep;
                var userThouSep = this.$store.getters['account/user'].MetricThousandSep;
                return this.conditionItem.fieldDisplayValue
                    .replaceAll(userThouSep, '')
                    .replaceAll(userDecSep, '.');
            },
            // setter
            set: function (newValue) {
                var userDecSep = this.$store.getters['account/user'].MetricDecimalSep;
                this.conditionItem.fieldDisplayValue = newValue.replaceAll('.', userDecSep);
            },
        },
        showValueField() {
            if (
                this.isEmptyNotEmptyOperator ||
                this.conditionItem.isParameterizable
            ) {
                return false;
            }
            if (this.conditionItem.fieldType == FieldType.StaticList) {
                return true;
            }
            if (
                this.conditionItem.fieldType == FieldType.DateTime &&
                this.conditionItem.useToday
            ) {
                return false;
            }
            if (this.conditionItem.fieldType != FieldType.DateTime && this.conditionItem.myselfOption !== null) {
                return false;
            }
            if (this.conditionItem.useVariables) return false;
            return true;
        },
        fieldType() {
            if (this.conditionItem.fieldCode != '') {
                var field = this.fieldList.find(
                    (x) => x.Code === this.conditionItem.fieldCode
                );
                if (field) {
                    this.conditionItem.fieldType = field.Type;
                    this.conditionItem.scale = field.Scale;
                }
            }
            return this.conditionItem.fieldType;
        },
        multiValue() {
            if (this.conditionItem.fieldCode != '') {
                var field = this.fieldList.find(
                    (x) => x.Code === this.conditionItem.fieldCode
                );
                if (field) {
                    this.conditionItem.multiValue = field.Multivalue;
                }
            }
            return this.conditionItem.multiValue;
        },
        hasPerformanceQuery() {
            this.conditionItem.hasPerformanceQuery = this.fieldList.some(
                (x) => x.Code === this.conditionItem.fieldCode && x.HasPerformanceQuery
            );
            return this.conditionItem.hasPerformanceQuery;
        }
    },
    watch: {
        search(val) {
            val !== this.conditionItem.fieldValue && this.filterListElements(val);
        },
        selectedFieldCode() {
            this.getListElements();
        },
        propsToWatch() {
            this.setFieldValue();
        },
        fieldList(val) {
            if (this.$refs.autocomplete)
                this.$refs.autocomplete.cachedItems = [];
            if (!val.some((x) => x.Code === this.conditionItem.fieldCode)) {
                this.conditionItem = new ConditionItem();
            }
        }
    },
    methods: {
        isCheckboxOptionVisible(checkbox) {
            switch (checkbox) {
            case this.checkboxValueOptions.MySelf:
            case this.checkboxValueOptions.MySelfId:
                if ((this.fieldType == 0 || this.fieldType >= 4) && !this.isEmptyNotEmptyOperator && this.showMyself && !this.conditionItem.isParameterizable) {
                    return true;
                } else return false;
            case this.checkboxValueOptions.Today:
                if (this.fieldType == 1 && !this.isEmptyNotEmptyOperator && this.showToday && !this.conditionItem.isParameterizable) {
                    return true;
                } else return false;
            case this.checkboxValueOptions.Variables:
                if (!this.canUseVariables) {
                    return false;
                } else if (!this.isEmptyNotEmptyOperator && (this.fieldType === FieldType.String || this.fieldType === FieldType.DateTime || this.fieldType === FieldType.Numeric)) {
                    return true;
                } else return false;
            default: return false;
            }
        },
        optionCheckboxSelect(checkbox) {
            switch (checkbox) {
            case this.checkboxValueOptions.MySelf:
                this.conditionItem.useVariables = false;
                break;
            case this.checkboxValueOptions.MySelfId:
                this.conditionItem.useVariables = false;
                this.changeMySelfId();
                break;
            case this.checkboxValueOptions.Today:
                this.conditionItem.useVariables = false;
                break;
            case this.checkboxValueOptions.Variables:
                this.conditionItem.useToday = false;
                this.conditionItem.fieldValue = null;
                this.conditionItem.fieldDisplayValue = null;
                this.conditionItem.myselfOption = null;
                break;
            }
        },
        validateNumericScale() {
            const number = parseFloat(this.fieldValueNumeric);
            const scale = this.fieldList.find((x) => x.Code === this.conditionItem.fieldCode).NumericScale || 0;
            this.fieldValueNumeric = Utils.number.truncate(number, scale).toString();
        },
        multivalueToString() {
            if (this.conditionItem.multiValue) {
                this.conditionItem.fieldDisplayValue = this.pivotAutoCompleteFieldValue.join(',');
                this.selectedItems = this.pivotAutoCompleteFieldValue;
            } else {
                this.conditionItem.fieldDisplayValue = this.pivotAutoCompleteFieldValue;
            }
        },
        multiValueToArray() {
            if (this.conditionItem.multiValue) {
                this.pivotAutoCompleteFieldValue = this.conditionItem.fieldDisplayValue
                    .split(',')
                    .filter((x) => x);
            } else {
                this.pivotAutoCompleteFieldValue = this.conditionItem.fieldDisplayValue;
            }
        },
        fieldCodeChange() {
            this.conditionItem.searchOperatorCode = null;
            this.conditionItem.myselfOption = null;
            this.conditionItem.useVariables = false;
            this.$v.$reset();

            this.$nextTick(() => {
                const defaultOperator = this.filteredSearchOperators.find(op =>
                    ['s04', 'd05', 'n05', 'l01'].includes(op.Code)
                );
                if (defaultOperator) {
                    this.conditionItem.searchOperatorCode = defaultOperator.Code;
                }
            });
        },
        getListElements() {
            if (this.clearValue) {
                this.conditionItem.fieldValue = '';
                this.conditionItem.fieldDisplayValue = '';
                if (this.$refs.autocomplete)
                    this.$refs.autocomplete.cachedItems = [];
            }
            if (this.conditionItem.fieldCode != '') {
                if (this.fieldType == FieldType.StaticList) {
                    this.listElements = this.fieldList.find(
                        (x) => x.Code === this.conditionItem.fieldCode
                    ).ListElements;
                    this.multiValueToArray();
                } else if (
                    this.fieldType == FieldType.DynamicList ||
                    this.fieldType == FieldType.DynamicTableList
                ) {
                    this.isLoading = true;
                    var filterQuery = { FirstTime: true };
                    this.getListElementService(this.conditionItem.fieldCode, filterQuery);
                }
            }
        },
        getListElementService(defFieldCode, filterQuery) {
            const fieldName = this.fieldList.find((x) => x.Code === defFieldCode)
                ? this.fieldList.find((x) => x.Code === defFieldCode).Name
                : '';

            ContentTypeService.getListElements(defFieldCode, filterQuery)
                .then((result) => {
                    if (result.length > 5000) {
                        Utils.toast.warning(this.$t('Site.DocumentFieldList.HighNumberOfRecordsDetected').replace('{0}', fieldName));
                    }

                    this.listElements = result;
                    this.multiValueToArray();
                })
                .finally((x) => (this.isLoading = false));
        },
        filterListElements(search) {
            if (this.fieldType == FieldType.StaticList) return;
            clearTimeout(this._timerId);
            this._timerId = setTimeout(() => {
                var filterQuery = { Filter: search };
                this.getListElementService(this.conditionItem.fieldCode, filterQuery);
            }, 500);
        },
        setFieldValue() {
            if (!this.conditionItem.useVariables) {
                this.conditionItem.fieldValue = this.conditionItem.fieldDisplayValue;
            }
            if (this.isEmptyNotEmptyOperator) {
                this.conditionItem.fieldValue = SearchConstValues.Empty;
            } else {
                switch (this.conditionItem.fieldType) {
                case FieldType.DateTime:
                    if (this.conditionItem.useToday) {
                        if (
                            this.conditionItem.daysBeforeAfterToday != '' &&
                            this.conditionItem.daysBeforeAfterToday > 0
                        ) {
                            this.conditionItem.fieldValue = `${
                                this.conditionItem.daysBeforeAfterToday
                            } ${
                                this.conditionItem.beforeAfter == 'B'
                                    ? SearchConstValues.DaysBeforeToday
                                    : SearchConstValues.DaysAfterToday
                            }`;
                        } else this.conditionItem.fieldValue = SearchConstValues.Today;
                    }
                    break;
                case FieldType.String:
                case FieldType.DynamicList:
                case FieldType.DynamicTableList:
                    if (this.conditionItem.myselfOption === MyselfOptionValues.MySelf) {
                        this.conditionItem.fieldValue = SearchConstValues.MySelf;
                    }
                    if (this.conditionItem.myselfOption === MyselfOptionValues.MySelf_Id) {
                        this.conditionItem.fieldValue = SearchConstValues.MySelf_Id;
                    }
                    break;
                }
            }
            return this.conditionItem.fieldValue;
        },
        reset() {
            this.$emit('on-condition-item-remove', this.conditionItem);
            this.conditionItem = new ConditionItem();
            this.selectedItems = [];
            this.pivotAutoCompleteFieldValue = [];
            this.$v.$reset();
        },
        onConditionItemAdd() {
            if (!this.showSearchOperatorSelect) {
                this.$emit('on-condition-item-add');
                return;
            }

            this.setFieldValue();
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            }
            var item = new ConditionItem(this.conditionItem.conditionStr());
            item.fieldType = this.fieldType;
            if (this.conditionItem.useVariables) {
                item.fieldDisplayValue = this.conditionItem.fieldDisplayValue;
            }
            if (this.$refs.autocomplete)
                this.$refs.autocomplete.cachedItems = [];
            this.$emit('on-condition-item-add', item);
        },
        changeMySelfId() {
            if (!this.conditionItem.searchOperatorCode || (this.conditionItem.searchOperatorCode !== 's04' && this.conditionItem.searchOperatorCode !== 's05')) {
                this.conditionItem.searchOperatorCode = 's04';
            }
        },
        loadEnabledAndDisabledInputs() {
            switch (this.origin) {
            case 1: //Actions modal
                this.showSearchOperatorSelect = false;
                this.clearValue = false;
                break;
            case 2: //Content type auto import modal
                this.showSearchOperatorSelect = false;
                this.showAddButton = false;
                this.clearValue = false;
                this.showMySelfId = false;
                break;
            case 3: //Actions modal with errors
                this.showSearchOperatorSelect = false;
                this.showParameterizableValue = true;
                this.clearValue = false;
                break;
            case 4: //Search criteria builder
                break;
            case 5: //Param search modal
                this.showAddButton = false;
                this.disableFieldSelect = true;
                this.disableSearchOperatorSelect = false;
                this.clearValue = false;
                break;
            default:
                break;
            }
        }
    },
};
</script>
<style lang="scss" scoped>
.search-criteria {
    .search-checkboxes {
        margin-top: 0;
    }
}
</style>